// doctor-frontend/src/components/LayoutWithSidebarAndHeader.js
// Nov 2025 patch (role-safe, single file for header + sidebar):
// - For interns: sidebar shows Start Activity, Activity Log, Roster, Guidelines Library
//   (no Self-Assessment, no Telemedicine, no duplicate tiles on page)
// - For non-intern reviewers, utility navigation uses role-neutral Roster & Posting Management / Roster View labels.
// - Preserves encounter FAB, visit-context hygiene, styles, and tenant-aware fetch
//
// Dec 2025 patch (no regression):
// ✅ Make ALL navigation tenant-safe (prevents redirect-to-login loops when a route drops /t/:slug)
// ✅ Make logout tenant-safe (redirect to /t/:slug/cmfm-login when in tenant mode) (chnged to /cmfm/login on 29 jan)
// ✅ Fix sidebar selection highlight in tenant paths by comparing "app path" (without /t/:slug)
//
// Dec 2025 patch (this update):
// ✅ If SPA navigation lands on a plain route (e.g., /intern/... without /t/:slug), auto-replace to /t/:slug/... on localhost
//    to prevent "Unknown tenant. Use subdomain or /t/:slug." flashes on first render.
//
// Jan 2026 patch (NO REGRESSION):
// ✅ DO NOT set X-Tenant-Id to slug (must be a Mongo ObjectId if present)
// ✅ Robust token lookup via Storage.key(i)
//
// Jan 2026 patch (NO REGRESSION):
// ✅ Allow horizontal scrolling inside main content (fixes wide tables like Roster)
//
// Jan 2026 patch (THIS UPDATE - NO REGRESSION):
// ✅ Enforce tenant prefix not only on localhost, but on production too *when we have a remembered slug*.
//    (Prevents random drops of /t/:slug when internal pages navigate to /dashboard/...)
//    - On production: DO NOT default to aiimsjodhpur-cmfm unless we have a remembered slug.
//    - On localhost: keep old fallback to aiimsjodhpur-cmfm.
//
// Jan 2026 patch (THIS UPDATE - NO REGRESSION):
// ✅ Admin dashboard group visibility fix: tolerate role variants like "CMFM Admin", "Admin User", etc.
// ✅ Sidebar highlight fix when menu item paths contain query strings
//
// MERGED (THIS FILE):
// ✅ Keeps tenant branding (tenantName, tenantDept)
// ✅ Keeps Admin tools block
// ✅ Keeps intern visit-context clear on /intern/*
// ✅ Keeps encounter FAB + event wiring + typing hygiene
//
// Apr 2026 cleanup patch (NO REGRESSION):
// ✅ Sidebar parent entries stay highlighted on nested routes (e.g. /dashboard/operations/*)
// ✅ Reviewer/admin utility blocks use same nested-route highlight logic
// ✅ Add explicit Main Navigation label without changing navigation structure
//
// Jul 2026 terminology guard (NO REGRESSION):
// ✅ Protect sidebar/user-facing labels from legacy EPA wording.
// ✅ Keep routes, role logic, tenant handling, visit FAB, returned-count bell, and admin utilities unchanged.
//
// Aug 2026 layout refinement (NO REGRESSION):
// ✅ Add persistent collapsible desktop sidebar with compact icon rail.
// ✅ Keep every existing intern/reviewer/admin navigation destination accessible in both states.
// ✅ Reclaim horizontal workspace across the portal while preserving tenant header, theme, logout, profile and FAB behavior.
//
// Aug 2026 returned-item badge cleanup (NO REGRESSION):
// ✅ Stop calling the retired /intern-activities/returned-count endpoint, which is not mounted by the current backend and produced repeated 400 tenant-resolution failures.
// ✅ Keep the established returned-visit count request as the primary source, with internActivities as the existing compatibility fallback.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import aiimsLogo from '../assets/aiims_logo.png';
import {
  Typography, Button, CircularProgress, Paper, Chip,
  IconButton, Badge, Tooltip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import { API_BASE } from '../services/http';

const groups = [
  // Mar 2026 patch (NO REGRESSION):
  // ✅ Merge service launchers into one sidebar entry pointing to the new comprehensive hub
  // ✅ Keep academics separate
  // ✅ Reduce reviewer-side duplicate launch points after Comprehensive Services becomes the main operational hub
  // ✅ Keep intern-special sidebar and admin block intact
  { label: 'Comprehensive Services',    path: '/dashboard/operations',   roles: ['junior resident','senior resident','faculty','hod','head of department','admin'] },
  { label: 'Academic Modules',          path: '/dashboard/academics',    roles: ['medical student','junior resident','senior resident','faculty','hod','head of department','admin'] },
];

const jget = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
const getLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

// ---- small safe helpers (NO regression) ----
const pathOnly = (p) => String(p || '').split('?')[0];
const normalizeRole = (r) => String(r || '')
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const formatNavigationLabel = (value) => {
  const input = value == null ? '' : String(value);
  if (!input) return '';
  return input
    .replace(/Entrustable\s+Professional\s+Activities\s*\(\s*EPAs\s*\)/gi, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/Entrustable\s+Professional\s+Activities/gi, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/\bEPAs\b/g, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/\bEPA\b/g, CORE_PRACTICE_AREA_LABEL);
};

const isPathActive = (currentPath, itemPath) => {
  const current = pathOnly(stripTenantPrefix(currentPath || '/'));
  const target = pathOnly(itemPath || '/');
  if (!target) return false;

  // Intern activity routes need explicit matching:
  // /intern/activity/new must not also highlight /intern/activity.
  if (target === '/intern/activity/new') {
    return (
      current === '/intern/activity/new' ||
      current.startsWith('/intern/activity/new/') ||
      current.startsWith('/intern/activity/run/')
    );
  }

  if (target === '/intern/activity') {
    return (
      current === '/intern/activity' ||
      current === '/intern/activity/log'
    );
  }

  if (current === target) return true;
  if (target !== '/' && current.startsWith(`${target}/`)) return true;
  return false;
};

function NavigationIcon({ label, path }) {
  const key = `${String(label || '').toLowerCase()} ${String(path || '').toLowerCase()}`;
  const props = { fontSize: 'small' };

  if (key.includes('/intern/activity/new') || key.includes('start activity')) return <PlayCircleOutlineIcon {...props} />;
  if (key.includes('/intern/activity') || key.includes('activity log')) return <AssignmentOutlinedIcon {...props} />;
  if (key.includes('roster')) return <CalendarMonthOutlinedIcon {...props} />;
  if (key.includes('guideline')) return <MenuBookOutlinedIcon {...props} />;
  if (key.includes('comprehensive') || key.includes('/dashboard/operations')) return <MedicalServicesOutlinedIcon {...props} />;
  if (key.includes('academic')) return <SchoolOutlinedIcon {...props} />;
  if (key.includes('approval') || key.includes('reviewer workbench')) return <FactCheckOutlinedIcon {...props} />;
  if (key.includes('manager') || key.includes('user profiles') || key.includes('user management')) return <ManageAccountsOutlinedIcon {...props} />;
  if (key.includes('/admin/') || key.includes('administration')) return <AdminPanelSettingsOutlinedIcon {...props} />;
  return <AppsOutlinedIcon {...props} />;
}

// ---- tenant/auth helpers (non-breaking) ----
function getTenantSlug() {
  // Prefer URL /t/:slug if present; otherwise fall back to last-known slug from storage.
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) return decodeURIComponent(m[1]);
  } catch {}
  return getLastKnownTenantSlug() || '';
}

function getLastKnownTenantSlug() {
  try {
    const w = window;
    const fromGlobal =
      w?.__tenantSlug ||
      w?.__TENANT_SLUG ||
      w?.__tenant?.slug ||
      w?.__tenant?.tenant?.slug;
    if (fromGlobal) return String(fromGlobal);
  } catch {}

  const tryKeys = [
    'tenantSlug',
    'tenant_slug',
    'activeTenantSlug',
    'active_tenant_slug',
    'portalTenantSlug',
    'portal_tenant_slug',
    'lastTenantSlug',
    'last_tenant_slug',
    'tenant',
    'activeTenant',
    'active_tenant',
  ];

  for (const k of tryKeys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;

      if (!raw.trim().startsWith('{') && raw.trim().length < 80) {
        return raw.trim();
      }

      if (raw.trim().startsWith('{')) {
        const obj = JSON.parse(raw);
        const slug =
          obj?.slug ||
          obj?.tenantSlug ||
          obj?.tenant_slug ||
          obj?.tenant?.slug ||
          obj?.tenant?.tenantSlug;
        if (slug) return String(slug);
      }
    } catch {}
  }

  try {
    const u = JSON.parse(localStorage.getItem('loggedInUser') || 'null');
    const slug =
      u?.tenantSlug ||
      u?.tenant_slug ||
      u?.tenant?.slug ||
      u?.tenant?.tenantSlug ||
      u?.org?.slug;
    if (slug) return String(slug);
  } catch {}

  return '';
}

function isObjectId(v) {
  return typeof v === 'string' && /^[a-f\d]{24}$/i.test(v.trim());
}

function getTenantId() {
  try {
    const candidates = [];

    const t1 = localStorage.getItem('tenantId');
    const t2 = localStorage.getItem('activeTenantId');
    const t3 = sessionStorage.getItem('tenantId');
    const t4 = sessionStorage.getItem('activeTenantId');
    if (t1) candidates.push(t1);
    if (t2) candidates.push(t2);
    if (t3) candidates.push(t3);
    if (t4) candidates.push(t4);

    const safeParse = (v) => { try { return JSON.parse(v); } catch { return null; } };

    const lu = safeParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeParse(sessionStorage.getItem('loggedInUser') || 'null');

    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantId,
        obj?.tenantId,
        core?.tenant?._id,
        core?.tenant?.id,
        obj?.tenant?._id,
        obj?.tenant?.id,
      );
    });

    const hit = candidates
      .map((x) => (x == null ? '' : String(x)).trim())
      .find((x) => isObjectId(x));

    return hit || '';
  } catch {
    return '';
  }
}

function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const k = store.key(i);
      if (k) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
}

function getAuthToken() {
  const pick = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferred = ['cmfm_token','tenant_token','portal_token','jwt','token','authToken'];
      for (const k of preferred) {
        const hit = keys.find((x) => String(x).toLowerCase() === String(k).toLowerCase());
        if (hit) return store.getItem(hit);
      }
      const nonOwnerKey = keys.find((k) => /token/i.test(k) && !/owner/i.test(k));
      if (nonOwnerKey) return store.getItem(nonOwnerKey);
      const any = keys.find((k) => /token/i.test(k));
      return any ? store.getItem(any) : null;
    } catch { return null; }
  };
  return pick(localStorage) || pick(sessionStorage);
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const p = String(path || '');
  if (!slug) return p;
  if (!p.startsWith('/')) return `/t/${slug}/${p}`.replace(/\/{2,}/g,'/');
  if (p.startsWith(`/t/${slug}`)) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g,'/');
}

function getCitizenPortalOrigin() {
  try {
    const env =
      process.env.REACT_APP_CITIZEN_PORTAL_ORIGIN ||
      process.env.REACT_APP_CITIZEN_PORTAL_URL ||
      process.env.REACT_APP_CITIZEN_PORTAL_BASE ||
      '';
    if (env) return String(env).trim();

    // Heuristic for production domains:
    // cmfm.<domain>  -> cmfm-citizen.<domain>
    const proto = (typeof window !== 'undefined' && window.location?.protocol) ? window.location.protocol : 'https:';
    const host = (typeof window !== 'undefined' && window.location?.hostname) ? window.location.hostname : '';
    const port = (typeof window !== 'undefined' && window.location?.port) ? window.location.port : '';
    if (!host) return '';

    if (host.startsWith('cmfm.')) {
      const rest = host.slice('cmfm.'.length);
      return `${proto}//cmfm-citizen.${rest}${port ? `:${port}` : ''}`;
    }

    return '';
  } catch {
    return '';
  }
}

function citizenPortalUrl(path) {
  const origin = getCitizenPortalOrigin();
  // Backward-compatible fallback: if not configured, behave like single-site build.
  if (!origin) return tenantUrl(path);
  const o = String(origin).replace(/\/+$/g, '');
  return `${o}${tenantUrl(path)}`;
}

function stripTenantPrefix(pathname) {
  try {
    const p = String(pathname || '');
    const m = p.match(/^\/t\/[^/]+(\/.*)?$/);
    return m ? (m[1] || '/') : p;
  } catch {
    return String(pathname || '');
  }
}

function apiUrl(path) {
  const p = String(path || '');
  if (/^https?:\/\//i.test(p)) return p;

  const normalizedPath = p.startsWith('/') ? p : `/${p}`;
  const base = String(API_BASE || '').trim().replace(/\/+$/g, '');

  return base ? `${base}${normalizedPath}` : normalizedPath;
}

async function fetchJson(path, { method='GET', json, headers } = {}) {
  const baseHeaders = { 'Content-Type':'application/json', ...(headers||{}) };
  const tok = getAuthToken();
  if (tok) baseHeaders.Authorization = `Bearer ${tok}`;

  const slug = getTenantSlug();
  const body = json ? JSON.stringify(json) : undefined;
  const plainPath = String(path || '');
  const requestUrl = apiUrl(plainPath);

  const doFetch = async (url, hdrs) => {
    const resp = await fetch(url, {
      method,
      headers: hdrs,
      body,
      credentials: 'include',
    });
    let data = {};
    try { data = await resp.json(); } catch {}
    return { resp, data };
  };

  if (slug) {
    const tenantHeaders = { ...baseHeaders, 'X-Tenant-Slug': slug };
    const tenantId = getTenantId();
    if (tenantId) tenantHeaders['X-Tenant-Id'] = tenantId;

    // API requests use the configured backend origin; tenant identity stays in headers.
    const first = await doFetch(requestUrl, tenantHeaders);

    if (first.resp.ok) return first.data;

    const msg = (first.data && (first.data.error || first.data.message)) || '';
    const isClientish =
      first.resp.status === 400 ||
      first.resp.status === 404 ||
      /unknown tenant/i.test(msg);

    if (isClientish) {
      const plainHeaders = { ...baseHeaders };
      delete plainHeaders['X-Tenant-Slug'];
      delete plainHeaders['X-Tenant-Id'];
      const second = await doFetch(requestUrl, plainHeaders);
      if (second.resp.ok) return second.data;

      const msg2 =
        (second.data && (second.data.error || second.data.message)) ||
        `Request failed: ${second.resp.status}`;
      const e = new Error(msg2);
      e.status = second.resp.status;
      e.data = second.data;
      throw e;
    }

    const finalMsg =
      (first.data && (first.data.error || first.data.message)) ||
      `Request failed: ${first.resp.status}`;
    const err = new Error(finalMsg);
    err.status = first.resp.status;
    err.data = first.data;
    throw err;
  }

  const plain = await doFetch(requestUrl, baseHeaders);
  if (plain.resp.ok) return plain.data;

  const finalMsg =
    (plain.data && (plain.data.error || plain.data.message)) ||
    `Request failed: ${plain.resp.status}`;
  const err = new Error(finalMsg);
  err.status = plain.resp.status;
  err.data = plain.data;
  throw err;
}

/** Clears lingering visit context on /intern/* and hides any "Visit not found" banner there */
function clearVisitContextOnIntern(navigate, location) {
  try {
    const VIS_KEYS = ['activeVisitId','activeVisit','currentVisitId','visitId','VIS_ID','VIS_ACTIVE','visitContext','visitHeader'];
    [localStorage, sessionStorage].forEach(store => {
      try {
        VIS_KEYS.forEach(k => store.removeItem(k));
        for (let i = 0; i < (store?.length || 0); i += 1) {
          const k = store.key(i);
          if (!k) continue;
          if (/visit/i.test(k) || /^VIS-/.test(k)) store.removeItem(k);
        }
      } catch {}
    });
    try { window.__activeVisit = null; } catch {}

    const sp = new URLSearchParams(location.search || '');
    let changed = false;
    ['visitId','visit','v','vid'].forEach(p => {
      if (sp.has(p)) { sp.delete(p); changed = true; }
    });
    if (changed) {
      navigate(
        { pathname: location.pathname, search: sp.toString() ? `?${sp}` : '' },
        { replace: true }
      );
    }
    try { window.dispatchEvent(new CustomEvent('visit:cleared')); } catch {}

    setTimeout(() => {
      try {
        const alerts = document.querySelectorAll('[role="alert"], .MuiAlert-root');
        alerts.forEach((el) => {
          const txt = (el.textContent || '').toLowerCase();
          if (txt.includes('visit not found')) {
            el.style.display = 'none';
            el.setAttribute('data-hidden-by', 'intern-visit-clear');
          }
        });
      } catch {}
    }, 0);
  } catch {}
}

export default function LayoutWithSidebarAndHeader() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ tenant-safe navigation wrapper
  const nav = (path) => {
    const p = String(path || '');
    const normalized = p.startsWith('/') ? p : `/${p}`;
    navigate(tenantUrl(normalized));
  };

  // ✅ Ensure tenant prefix stays present even if some component navigates to plain routes
  useEffect(() => {
    try {
      const path = location.pathname || '';
      const hasTenant = /^\/t\/[^/]+(\/|$)/.test(path);
      const isOwner = /^\/owner(\/|$)/.test(path);
      const isLocal = /^localhost(:\d+)?$/i.test(window.location.host);

      if (!hasTenant && !isOwner) {
        const remembered = getLastKnownTenantSlug();
        const slug = remembered || (isLocal ? 'aiimsjodhpur-cmfm' : '');

        if (slug) {
          const next = `/t/${encodeURIComponent(slug)}${path}${location.search || ''}`;
          navigate(next.replace(/\/{2,}/g, '/'), { replace: true });
        }
      }
    } catch {}
  }, [location.pathname, location.search, navigate]);

  // theme
  const [themeMode, setThemeMode] = useState(() => getLS('theme') || 'dark');

  // Tenant branding (multi-tenant safe)
  const tenantInfo = (() => {
    try {
      return window.__tenant || window.__TENANT || window.__tenantInfo || null;
    } catch {
      return null;
    }
  })();
  const tenantName = tenantInfo?.name || 'AIIMS Jodhpur';
  const tenantDept = tenantInfo?.dept || 'Department of Community Medicine & Family Medicine';

  // Sidebar header identity
  const [userName, setUserName] = useState(() => {
    const u = jget('loggedInUser') || {};
    return u?.name || u?.user?.name || 'User';
  });
  const [userRole, setUserRole] = useState(() => {
    const u = jget('loggedInUser') || {};
    return u?.role || u?.userType || u?.user?.role || 'User';
  });
  const [currentCentre, setCurrentCentre] = useState(() => {
    const u = jget('loggedInUser') || {};
    return u?.centre || u?.center || u?.user?.centre || u?.user?.center || '';
  });
  const [currentCentreSource, setCurrentCentreSource] = useState(() => currentCentre ? 'default' : '');

  // ✅ Normalize role safely (fixes admin variants)
  const roleLower = normalizeRole(userRole);
  const isIntern = roleLower.includes('intern');
  const isAdmin =
    ['admin', 'administrator', 'super admin', 'superadmin', 'cmfm admin', 'admin user'].includes(roleLower) ||
    roleLower.endsWith(' admin');
  // JR-1/JR-2/JR-3 are intentionally not differentiated yet; all use the safe JR reviewer baseline.
  // SR/Faculty/HOD retain reviewer/oversight navigation; data scope remains tenant/backend controlled.
  const isReviewer = ['junior resident','senior resident','faculty','hod','head of department','admin','jr','sr']
    .some(r => roleLower.includes(String(r || '').toLowerCase()));

  // Persistent collapsible sidebar. Expanded remains the default for existing users.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => getLS('cmfmSidebarCollapsed') === '1');
  const sidebarWidth = sidebarCollapsed ? 80 : 224;

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      try { localStorage.setItem('cmfmSidebarCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // theme apply
  useEffect(() => {
    document.documentElement.classList.toggle('dark', themeMode === 'dark');
    localStorage.setItem('theme', themeMode);
  }, [themeMode]);

  // Resolve the current operational centre from the dated roster for Intern/JR/SR.
  // For other roles, or when no active posting exists, keep the user's configured
  // default centre visible rather than hiding centre context completely.
  useEffect(() => {
    let alive = true;

    const user = jget('loggedInUser') || {};
    const configuredCentre =
      user?.centre ||
      user?.center ||
      user?.user?.centre ||
      user?.user?.center ||
      '';

    const needsDatedPosting =
      roleLower.includes('intern') ||
      roleLower.includes('junior resident') ||
      roleLower.includes('senior resident') ||
      roleLower === 'jr' ||
      roleLower === 'sr';

    async function resolveVisibleCentre() {
      if (!needsDatedPosting) {
        if (!alive) return;
        setCurrentCentre(configuredCentre || '');
        setCurrentCentreSource(configuredCentre ? 'default' : '');
        return;
      }

      const today = new Date().toISOString().slice(0, 10);

      try {
        const rosterPayload = await fetchJson(
          `/api/rosters/mine?from=${encodeURIComponent(today)}&to=${encodeURIComponent(today)}&limit=20`
        );
        const rows = Array.isArray(rosterPayload)
          ? rosterPayload
          : Array.isArray(rosterPayload?.rows)
            ? rosterPayload.rows
            : Array.isArray(rosterPayload?.items)
              ? rosterPayload.items
              : Array.isArray(rosterPayload?.data)
                ? rosterPayload.data
                : [];

        const active = rows.find((row) =>
          row?.centre &&
          row?.isOnDuty !== false &&
          row?.isOnLeave !== true
        );

        if (active?.centre) {
          if (!alive) return;
          setCurrentCentre(String(active.centre));
          setCurrentCentreSource('roster');
          return;
        }
      } catch {}

      if (!alive) return;
      setCurrentCentre(configuredCentre || '');
      setCurrentCentreSource(configuredCentre ? 'default' : '');
    }

    resolveVisibleCentre();

    const onRosterUpdated = () => resolveVisibleCentre();
    window.addEventListener('roster:updated', onRosterUpdated);
    return () => {
      alive = false;
      window.removeEventListener('roster:updated', onRosterUpdated);
    };
  }, [roleLower]);

  // Encounter FAB state
  const [encCan, setEncCan] = useState(false);
  const [encBusy, setEncBusy] = useState(false);
  const [encVisit, setEncVisit] = useState('');
  const [lastTypingTs, setLastTypingTs] = useState(0);

  // Returned bell
  const [retCount, setRetCount] = useState(0);
  const [retLoading, setRetLoading] = useState(false);

  // Detect encounter page (keep broad compatibility)
  const path = location.pathname || '';
  const isEncounterPath = /\/citizen\/[^/]+\/encounter\/(medical-history|history|examination|exam|investigations|investigation|prescription|prescriptions)/i.test(path)
    // Support alternate encounter routes if any exist (without enabling on dashboards)
    || /\/encounter\/(medical-history|history|examination|exam|investigations|investigation|prescription|prescriptions)/i.test(path);

  const pageKey = useMemo(() => {
    const step = (/\/encounter\/([^/?#]+)/.exec(path || '')?.[1] || '').toLowerCase();
    if (['history','examination','exam','investigations','prescriptions','prescription'].includes(step)) {
      if (step === 'examination' || step === 'exam') return 'exam';
      if (step === 'prescription'  || step === 'prescriptions') return 'prescriptions';
      return step;
    }
    if (path.includes('/medical-history')) return 'history';
    if (path.includes('/examination'))     return 'exam';
    if (path.includes('/investigations'))  return 'investigations';
    if (path.includes('/prescriptions') || path.includes('/prescription')) return 'prescriptions';
    return '';
  }, [path]);

  const isInternOnRx = isIntern && pageKey === 'prescriptions';

  // Keep typing hygiene
  useEffect(() => {
    const isTextAccepting = (el) => {
      if (!el) return false;
      if (el.isContentEditable) return true;
      const tag = (el.tagName || '').toLowerCase();
      if (tag === 'textarea' || tag === 'select') return true;
      if (tag !== 'input') return false;
      const type = (el.type || '').toLowerCase();
      return ['text','search','email','url','tel','password','number','date','datetime-local','time','month','week'].includes(type) || !type;
    };
    const onKey = (e) => {
      if (e.isComposing || e.keyCode === 229) return;
      const key = e.key;
      if (key !== 'Enter' && key !== ' ') return;
      if (isTextAccepting(e.target)) setLastTypingTs(Date.now());
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // Reset encounter state when leaving encounter area
  useEffect(() => {
    if (!isEncounterPath) {
      setEncCan(false);
      setEncBusy(false);
      setEncVisit('');
    }
  }, [isEncounterPath]);

  // Track visitId from storage (keeps old behavior)
  useEffect(() => {
    try {
      const v = getLS('activeVisitId') || getLS('visitId') || '';
      setEncVisit(v || '');
      if (v) setEncCan(true);
    } catch {}
    // eslint-disable-next-line
  }, [location.pathname, location.search]);

  // Wire encounter events to FAB (keeps newer behavior)
  useEffect(() => {
    const onCan = (e) => {
      const d = e?.detail || {};
      const enabled = d.can ?? d.canContinue ?? d.enabled;
      setEncCan(Boolean(enabled));
    };
    const onBusy = (e) => setEncBusy(Boolean(e?.detail?.busy));
    const onVisit = (e) => {
      const v = e?.detail?.visitId || e?.detail?.id || '';
      if (v) setEncVisit(String(v));
    };
    const onDisable = () => setEncCan(false);

    window.addEventListener('encounter:can-continue', onCan);
    window.addEventListener('encounter:busy', onBusy);
    window.addEventListener('encounter:visit', onVisit);
    window.addEventListener('encounter:disable', onDisable);
    return () => {
      window.removeEventListener('encounter:can-continue', onCan);
      window.removeEventListener('encounter:busy', onBusy);
      window.removeEventListener('encounter:visit', onVisit);
      window.removeEventListener('encounter:disable', onDisable);
    };
  }, []);

  // Clear visit context on /intern/*
  useEffect(() => {
    const pth = location.pathname || '';
    const isTenantIntern = /^\/t\/[^/]+\/intern(\/|$)/i.test(pth);
    const isPlainIntern  = /^\/intern(\/|$)/i.test(pth);
    const onInternArea   = isTenantIntern || isPlainIntern;
    if (onInternArea && !isEncounterPath) clearVisitContextOnIntern(navigate, location);
    // eslint-disable-next-line
  }, [location.pathname, location.search]);

  // Route change → scroll to top
  useEffect(() => {
    try {
      const main = document.querySelector('main');
      if (main) main.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch {}
  }, [location.pathname, location.search]);

  // Update user identity on profile/storage changes
  useEffect(() => {
    const onProfileUpdated = (e) => {
      const p = e?.detail || {};
      if (p.displayName) setUserName(p.displayName);
      if (p.designation) setUserRole(p.designation);
    };
    const onStorage = (e) => {
      if (e.key && e.key !== 'loggedInUser') return;
      const u = jget('loggedInUser') || {};
      setUserName(u?.name || u?.user?.name || 'User');
      setUserRole(u?.role || u?.userType || u?.user?.role || 'User');
      const configuredCentre = u?.centre || u?.center || u?.user?.centre || u?.user?.center || '';
      if (configuredCentre && currentCentreSource !== 'roster') {
        setCurrentCentre(configuredCentre);
        setCurrentCentreSource('default');
      }
    };
    window.addEventListener('profile:updated', onProfileUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('profile:updated', onProfileUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, [currentCentreSource]);

  // Returned count (intern only).
  // The old /api/intern-activities/returned-count route is not mounted by the
  // current backend. Calling it from a tenant URL produced repeated 400
  // "Unknown tenant" requests before falling through to the working sources.
  // Use the already-supported returned-visit count first, while retaining the
  // internActivities query as a compatibility fallback.
  const loadReturnedCount = async () => {
    if (!isIntern) { setRetCount(0); return; }
    try {
      setRetLoading(true);

      const countParams = {
        mine: '1',
        status: 'returned',
        limit: '1',
        count: '1',
      };
      const qs = new URLSearchParams(countParams).toString();

      let data = null;
      try {
        data = await fetchJson(`/api/visits?${qs}`);
      } catch {
        const qs2 = new URLSearchParams(countParams).toString();
        data = await fetchJson(`/api/internActivities?${qs2}`);
      }

      const n =
        (typeof data?.count === 'number' && data.count) ||
        (typeof data?.total === 'number' && data.total) ||
        (Array.isArray(data?.items) ? data.items.length : 0) ||
        0;

      setRetCount(Number.isFinite(Number(n)) ? Number(n) : 0);
    } catch {
      setRetCount(0);
    } finally {
      setRetLoading(false);
    }
  };

  useEffect(() => {
    loadReturnedCount();
    const onFocus = () => loadReturnedCount();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line
  }, [isIntern]);

  const handleLogout = async () => {
    // This layout belongs to the CMFM/staff application. Routes such as
    // /citizen/:citizenId/visit/... are clinician-side CMFM routes and must
    // never be mistaken for the separate Citizen Portal during logout.
    try { window.__cmfmLogoutInProgress = true; } catch {}

    try {
      const mod = await import('../services/auth');
      if (mod?.logout) await mod.logout();
    } catch {
      // Defensive fallback if the auth helper fails to load: clear only
      // CMFM/staff authentication keys. Do not clear citizen portal keys.
      const cmfmKeys = [
        'cmfm_token', 'cmfmToken', 'cmfm_user', 'cmfmUser',
        'authToken', 'token', 'jwt', 'accessToken',
        'tenant_token', 'tenantToken', 'portal_token', 'portalToken',
        'cmfm_jwt', 'role', 'userRole',
      ];

      [localStorage, sessionStorage].forEach((store) => {
        try {
          cmfmKeys.forEach((key) => store.removeItem(key));
          const raw = store.getItem('loggedInUser');
          if (raw) {
            let payload = null;
            try { payload = JSON.parse(raw); } catch {}
            const core = payload?.user || payload || {};
            const role = String(core?.role || payload?.role || '').toLowerCase().trim();
            if (role !== 'citizen') store.removeItem('loggedInUser');
          }
        } catch {}
      });
    }

    setEncCan(false); setEncBusy(false); setEncVisit('');

    // Always return staff users to the tenant-aware CMFM login. Use replace so
    // Back cannot reopen the protected screen after logout.
    window.location.replace(tenantUrl('/cmfm/login'));
  };

  const visibleGroups = useMemo(() => {
    if (isIntern) {
      return [
        { label: 'Start Activity',     path: '/intern/activity/new' },
        { label: 'Activity Log',       path: '/intern/activity' },
        { label: 'Roster / Postings',  path: '/intern/roster' },
        { label: 'Guidelines Library', path: '/intern/guidelines' },
      ];
    }

    // ✅ tolerant role matching (important for Admin variants)
    // Keep top-level navigation minimal: Comprehensive Services is the primary launch hub for non-intern workflows.
    return groups.filter((g) => (g.roles || []).some((r) => roleLower.includes(String(r || '').toLowerCase())));
  }, [isIntern, roleLower]);

  const toggleTheme = () => {
    const next = themeMode === 'dark' ? 'light' : 'dark';
    document.documentElement.classList.toggle('dark', next === 'dark');
    localStorage.setItem('theme', next);
    setThemeMode(next);
  };

  return (
    <div className="flex h-screen overflow-hidden text-black dark:text-white bg-[#dcecf4] dark:bg-[#121212] transition-colors duration-300">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 bg-[#e36606] dark:bg-gradient-to-b dark:from-[#232326] dark:to-[#19191b] text-white h-full flex flex-col z-40 overflow-hidden ${sidebarCollapsed ? 'px-2 py-3' : 'p-4'}`}
        style={{ width: sidebarWidth, transition: 'width 220ms ease, padding 220ms ease' }}
      >
        <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: sidebarCollapsed ? 24 : 8,
              color: '#fffbe6',
              bgcolor: 'rgba(0,0,0,0.18)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.30)' },
              zIndex: 2,
            }}
          >
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>

        {/* Sidebar header block */}
        <div className="shrink-0">
          <div className={`${sidebarCollapsed ? 'mt-10 mb-3' : 'mt-6 mb-4'} text-center`}>
            <Tooltip
              title={sidebarCollapsed ? `${tenantName} · ${userName} · ${userRole} · Centre: ${currentCentre || 'Not posted'}` : ''}
              placement="right"
              disableHoverListener={!sidebarCollapsed}
            >
              <img
                src={aiimsLogo}
                alt="AIIMS Logo"
                className={`${sidebarCollapsed ? 'w-12 h-12' : 'w-28 h-28'} mx-auto mb-2 rounded-full border-4 border-[#fff4e1] bg-white object-cover`}
                style={{ boxShadow: sidebarCollapsed ? '0 0 0 2px #ffd4ae' : '0 0 0 4px #ffd4ae' }}
              />
            </Tooltip>

            {!sidebarCollapsed ? (
              <>
                <div className="text-sm font-semibold" style={{ color: '#fffbe6' }}>{tenantName}</div>
                <div className="text-xs" style={{ color: '#fff' }}>Welcome, {userName}</div>
                <div className="text-xs" style={{ color: '#fff0db' }}>Role: {userRole}</div>
                <div className="text-xs mb-1" style={{ color: '#fffbe6' }}>
                  Centre: {currentCentre || 'Not posted'}
                  {currentCentreSource === 'default' ? ' (default)' : ''}
                </div>
                <button
                  onClick={() => nav(tenantUrl('/profile/me'))}
                  className="mt-2 text-xs text-white underline hover:text-[#ffe1c3]"
                  style={{ fontWeight: 'bold' }}
                >
                  My Profile
                </button>
                {isAdmin ? (
                  <button
                    onClick={() => nav(tenantUrl('/dashboard/admin/users'))}
                    className="mt-1 text-xs text-white underline hover:text-[#ffe1c3]"
                    style={{ fontWeight: 'bold' }}
                    title="Open admin user management (profiles can be opened/edited from here)"
                  >
                    User Profiles (Admin)
                  </button>
                ) : null}
              </>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Tooltip title="My Profile" placement="right">
                  <IconButton onClick={() => nav(tenantUrl('/profile/me'))} size="small" sx={{ color: '#fffbe6' }}>
                    <PersonOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {isAdmin ? (
                  <Tooltip title="User Profiles (Admin)" placement="right">
                    <IconButton onClick={() => nav(tenantUrl('/dashboard/admin/users'))} size="small" sx={{ color: '#fffbe6' }}>
                      <ManageAccountsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable menu area */}
        <div className="flex-1 overflow-y-auto pr-1">
          <nav className="space-y-2">
            {!sidebarCollapsed ? (
              <div
                className="text-[11px] uppercase tracking-wide opacity-90 mb-1"
                style={{ color: '#fffbe6' }}
              >
                Main Navigation
              </div>
            ) : null}
            {visibleGroups.map((group, idx) => {
              const groupPathOnly = pathOnly(group.path);
              const active = isPathActive(location.pathname, groupPathOnly);
              const displayLabel = formatNavigationLabel(group.label);
              return (
                <Tooltip
                  key={idx}
                  title={displayLabel}
                  placement="right"
                  disableHoverListener={!sidebarCollapsed}
                >
                  <div
                    onClick={() => nav(group.path)}
                    className={`cursor-pointer flex items-center text-sm rounded-xl shadow-sm transition-all duration-200 bg-white dark:bg-[#232326] text-black dark:text-white hover:bg-[#ffe1c6] dark:hover:bg-[#31313a] font-medium ${
                      sidebarCollapsed ? 'justify-center p-2.5' : 'gap-2 p-2'
                    } ${active ? 'ring-2 ring-[#e36606] dark:ring-[#ffd180]' : ''}`}
                    style={{ fontWeight: active ? 700 : 500 }}
                  >
                    <span className="shrink-0"><NavigationIcon label={displayLabel} path={group.path} /></span>
                    {!sidebarCollapsed ? <span className="truncate">{displayLabel}</span> : null}
                  </div>
                </Tooltip>
              );
            })}

            {/* Reviewer utilities block (trimmed so Comprehensive Services remains the main operational hub) */}
            {(!isIntern && isReviewer) && (
              <div className="mt-4">
                {!sidebarCollapsed ? (
                  <div
                    className="text-[11px] uppercase tracking-wide opacity-90 mb-1"
                    style={{ color: '#fffbe6' }}
                  >
                    Reviewer Utilities
                  </div>
                ) : null}

                {[
                  { label: 'Reviewer Workbench',             path: '/dashboard/approval' },
                  { label: 'Roster & Posting Management',    path: '/intern/manager' },
                  { label: 'Roster View',                     path: '/intern/roster' },
                  { label: 'Guidelines Library',              path: '/intern/guidelines' },
                ].map((item, idx2) => {
                  const itemPathOnly = pathOnly(item.path);
                  const active = isPathActive(location.pathname, itemPathOnly);
                  const displayLabel = formatNavigationLabel(item.label);
                  return (
                    <Tooltip
                      key={idx2}
                      title={displayLabel}
                      placement="right"
                      disableHoverListener={!sidebarCollapsed}
                    >
                      <div
                        onClick={() => nav(item.path)}
                        className={`cursor-pointer flex items-center text-sm rounded-xl bg-white dark:bg-[#232326] text-black dark:text-white hover:bg-[#ffe1c6] dark:hover:bg-[#31313a] mb-1 ${
                          sidebarCollapsed ? 'justify-center p-2.5' : 'gap-2 p-2'
                        } ${active ? 'ring-2 ring-[#e36606] dark:ring-[#ffd180]' : ''}`}
                      >
                        <span className="shrink-0"><NavigationIcon label={displayLabel} path={item.path} /></span>
                        {!sidebarCollapsed ? <span className="truncate">{displayLabel}</span> : null}
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            )}

            {/* Admin tools block (admin only) */}
            {isAdmin && (
              <div style={{ marginTop: 12 }}>
                {!sidebarCollapsed ? (
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      opacity: 0.9,
                      marginBottom: 6,
                      color: '#fffbe6',
                    }}
                  >
                    Admin
                  </div>
                ) : null}

                {[
                  { label: 'Activity / Core Practice Area Administration', path: '/admin/activities' },
                  { label: 'User Management',    path: '/admin/users' },
                ].map((item) => {
                  const itemPathOnly = pathOnly(item.path);
                  const active = isPathActive(location.pathname, itemPathOnly);
                  const displayLabel = formatNavigationLabel(item.label);
                  return (
                    <Tooltip
                      key={item.path}
                      title={displayLabel}
                      placement="right"
                      disableHoverListener={!sidebarCollapsed}
                    >
                      <button
                        onClick={() => nav(item.path)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                          gap: sidebarCollapsed ? 0 : 8,
                          textAlign: 'left',
                          padding: sidebarCollapsed ? '10px 8px' : '10px 12px',
                          borderRadius: 14,
                          border: active ? '2px solid #e36606' : '1px solid rgba(255,255,255,0.22)',
                          background: active ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.20)',
                          color: active ? '#111' : '#fff',
                          fontWeight: 700,
                          marginBottom: 8,
                          cursor: 'pointer',
                        }}
                        type="button"
                      >
                        <NavigationIcon label={displayLabel} path={item.path} />
                        {!sidebarCollapsed ? <span>{displayLabel}</span> : null}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </nav>
        </div>

        {/* Sidebar footer */}
        <div className={`text-center shrink-0 pt-2 ${sidebarCollapsed ? 'space-y-2' : 'space-y-4'}`}>
          {sidebarCollapsed ? (
            <>
              <Tooltip title={themeMode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'} placement="right">
                <IconButton
                  onClick={toggleTheme}
                  size="small"
                  sx={{ color: '#fffbe6', border: '1px solid rgba(255,255,255,0.28)' }}
                >
                  {themeMode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Logout" placement="right">
                <IconButton onClick={handleLogout} size="small" sx={{ color: '#fecaca' }}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <button
                onClick={toggleTheme}
                className="text-xs px-2 py-1 rounded font-semibold border-2 shadow-md bg-white text-[#e36606] border-[#e36606] hover:bg-[#ffecd9] hover:text-[#b84801] dark:bg-[#0f172a] dark:text-[#ffd7a3] dark:border-[#f59e0b] dark:hover:bg-[#1f2937] dark:hover:text-[#ffe3b3]"
              >
                {themeMode === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button onClick={handleLogout} className="text-sm text-red-200 hover:underline" type="button">Logout</button>
            </>
          )}
        </div>
      </aside>

      {/* Main */}
      <div
        className="flex-1 flex flex-col min-w-0"
        style={{ marginLeft: sidebarWidth, transition: 'margin-left 220ms ease' }}
      >
        <header
          className="fixed top-0 right-0 bg-[#e36606] dark:bg-gradient-to-r dark:from-[#232326] dark:to-[#19191b] text-white flex items-center justify-between px-12 h-[110px] shadow-md z-30 transition-colors duration-300"
          style={{
            left: sidebarWidth,
            minHeight: '110px',
            maxHeight: '110px',
            transition: 'left 220ms ease',
          }}
        >
          <Typography
            variant="h5"
            className="text-3xl font-playfair text-left"
            style={{ color: '#fffbe6', fontWeight: 700 }}
          >
            {tenantDept}
          </Typography>

          <div className="flex items-center gap-3">
            {sidebarCollapsed && (
              <Chip
                size="small"
                label={`Centre: ${currentCentre || 'Not posted'}`}
                variant="outlined"
                sx={{
                  color: '#fffbe6',
                  borderColor: 'rgba(255,251,230,0.75)',
                  '& .MuiChip-label': { fontWeight: 700 },
                }}
              />
            )}
          {isIntern && (
            <Tooltip title={retLoading ? 'Checking returned items…' : 'Returned to you'}>
              <IconButton
                size="large"
                onClick={() => nav('/intern/activity?filter=returned')}
                sx={{ color: '#fffbe6' }}
              >
                <Badge
                  color="error"
                  badgeContent={retCount > 99 ? '99+' : retCount}
                  invisible={!retCount}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          </div>
        </header>

        {/* ✅ enable horizontal scroll for wide pages (Roster table etc.) */}
        <main className="pt-[110px] p-0 overflow-y-auto overflow-x-auto h-full min-w-0 transition duration-300 text-black dark:text-white">
          <div className="bg-cover bg-center bg-no-repeat bg-fixed min-h-full min-w-0 bg-[#dcecf4] dark:bg-[#121212]">
            <div
              className={`p-6 rounded-xl min-h-screen min-w-0 transition-colors duration-300 ${
                themeMode === 'dark' ? 'bg-[#232326] text-white' : 'bg-[#e7f2f8] text-black'
              }`}
            >
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Floating Encounter FAB */}
      {roleLower !== 'citizen' && isEncounterPath && (
        <Paper
          id="enc-fab"
          elevation={6}
          className="fixed bottom-6 right-6 z-40"
          sx={{ p: 1, borderRadius: 999, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Button
            type="button"
            variant="contained"
            onMouseDown={(e) => {
              if (Date.now() - lastTypingTs < 400) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            disabled={!(encCan || Boolean(encVisit)) || encBusy}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
            }}
            onClick={() => {
              if (Date.now() - lastTypingTs < 400) return;
              const v = encVisit || getLS('activeVisitId') || getLS('visitId') || '';
              window.dispatchEvent(
                new CustomEvent('encounter:save', { detail: { visitId: v, intent: 'save-continue' } })
              );
            }}
            startIcon={encBusy ? <CircularProgress size={16} /> : null}
            sx={{ borderRadius: 999, px: 2 }}
          >
            {isInternOnRx ? 'Send for approval' : 'Save & Continue'}
          </Button>
        </Paper>
      )}
    </div>
  );
}
